<template>
  <section class="container mx-auto px-4 md:px-8 py-8">
    <h1 class="text-2xl md:text-3xl font-bold mb-6">Кошик</h1>

    <div v-if="!cart.items.length" class="text-gray-500">
      Ваш кошик порожній.
      <NuxtLink to="/catalog" class="underline ml-1"
        >Перейти до каталогу</NuxtLink
      >
    </div>

    <div v-else class="grid lg:grid-cols-3 gap-8">
      <div class="lg:col-span-2 space-y-3">
        <!-- Skeleton only for first availability resolve to avoid visible sorting jump -->
        <CartSkeleton v-if="showSkeleton" />

        <CartItemRow
          v-for="it in sortedItems"
          v-else
          :key="it.key"
          :item="it"
          :in-stock="isItemInStock(it)"
          @inc="inc(it)"
          @dec="dec(it)"
          @remove="removeItem(it)"
          @qty-input="onQtyInput(it, $event)"
        />
      </div>

      <CartSummary
        :total-qty="totalQty"
        :total-sum="totalSum"
        @checkout="goCheckout"
        @clear="clearAll"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useCartStore } from "~/stores/cart";
import { useAuthStore } from "~/stores/auth";
import { useToast } from "~/composables/useToast";
import { useTracking, buildPixelContents } from "~/composables/useTracking";
import { useCartAvailability } from "~/composables/cart/useCartAvailability";

definePageMeta({ ssr: false });

const cart = useCartStore();
const auth = useAuthStore();
const router = useRouter();
const toast = useToast();

const {
  showSkeleton,
  refreshAvailability,
  isItemInStock,
  getMaxQty,
  stopSkeleton,
} = useCartAvailability();

function toastMax(max) {
  toast.show(`Можна додати максимум ${max} шт`, { type: "error" });
}

const sortedItems = computed(() => {
  const items = Array.isArray(cart.items) ? [...cart.items] : [];
  return items
    .map((x, i) => ({ x, i, inStock: isItemInStock(x) }))
    .sort((a, b) => {
      if (a.inStock !== b.inStock) return a.inStock ? -1 : 1;
      return a.i - b.i;
    })
    .map((w) => w.x);
});

// Живий кошик і всі записи — у сторі (stores/cart). Сторінка лише
// підписується на час свого життя і перераховує наявність.
onMounted(() => {
  cart.subscribeRemote();
});

watch(
  () => cart.items,
  (items) => {
    refreshAvailability(items);
  },
  { deep: true, immediate: true },
);

onUnmounted(() => {
  cart.unsubscribeRemote();
  stopSkeleton();
});

const { pixel: mpTrack } = useTracking();

function inc(it) {
  // якщо явно OOS/неопубліковано — не даємо збільшувати
  if (!isItemInStock(it)) {
    toast.show("Немає в наявності", { type: "error" });
    return;
  }

  const max = getMaxQty(it);
  if (typeof max === "number") {
    const next = Number(it.qty || 0) + 1;
    if (next > max) {
      toastMax(max);
      return;
    }
  }

  cart.setQty(it.key, Number(it.qty) + 1);
}
function dec(it) {
  // 0 у сторі означає видалення позиції
  cart.setQty(it.key, Number(it.qty) - 1);
}
function onQtyInput(it, v) {
  const max = getMaxQty(it);
  if (typeof max === "number" && v > max) {
    toastMax(max);
    cart.setQty(it.key, max);
    return;
  }

  cart.setQty(it.key, v);
}
async function removeItem(it) {
  await cart.remove(it.key);
}
async function clearAll() {
  await cart.clear();
}
async function pruneOutOfStock() {
  const oos = (cart.items || []).filter((it) => !isItemInStock(it));
  if (!oos.length) return;
  await cart.removeMany(oos.map((it) => it.key));
}

async function goCheckout() {
  // Clean up out-of-stock items BEFORE login/checkout
  await pruneOutOfStock();

  if (!auth.isAuthed) return router.push("/login?next=/checkout");

  const items = Array.isArray(cart.items) ? cart.items : [];
  const { content_ids, contents, num_items } = buildPixelContents(items);
  const value = items.reduce(
    (s, i) => s + Number(i?.price || 0) * Number(i?.qty || 0),
    0,
  );

  if (num_items > 0) {
    mpTrack("InitiateCheckout", {
      currency: "UAH",
      value: Number(value || 0),
      num_items,
      content_type: "product",
      content_ids,
      contents,
    });
  }

  router.push("/checkout");
}

const totalQty = computed(() =>
  (cart.items || []).reduce((a, i) => a + Number(i.qty || 0), 0),
);
const totalSum = computed(() =>
  (cart.items || []).reduce(
    (a, i) => a + Number(i.qty || 0) * Number(i.price || 0),
    0,
  ),
);
</script>
